/**
 * @fileoverview Signals in Action Page - TradingView Integration
 * 
 * This page demonstrates the predictive power of CAP signals by showing
 * them alongside market charts. Two modes:
 * 
 * 1. **Advanced Charts Mode** (preferred) - True overlay with CAP datafeed
 * 2. **Widget Mode** (fallback) - Stacked charts approach
 * 
 * @module pages/signals-in-action
 */

import { el } from '../shared/components/simpleRender.js';
import { FEATURES } from '../core/features.js';
import {
  // Advanced Charts (preferred)
  createAdvancedChart,
  loadAdvancedCharts,
  isAdvancedChartsLoaded,
  CAP_EXCHANGE,
  // Widget mode (fallback)
  createMarketChart,
  loadTradingView,
  isTradingViewLoaded,
  // Shared
  SIGNAL_TO_SYMBOL,
} from '../external/tradingview/index.js';
import {
  fetchSignal,
  fetchSignalHistory,
  SIGNAL_METADATA,
  CATEGORIES,
  toChartData,
} from '../api/capitulators.js';
import { createExpandedChart } from '../signals/components/SparklineChart.js';

// ============================================================================
// CORRELATION PAIRS - Signal + Market combinations that tell a story
// ============================================================================

/**
 * Curated signal-market pairs with correlation narratives
 */
const CORRELATION_PAIRS = [
  {
    signal: 'NET_LIQUIDITY',
    market: 'FOREXCOM:SPXUSD',
    marketName: 'S&P 500',
    narrative: 'Fed liquidity injections tend to lead equity prices by 2-4 weeks. When NET_LIQUIDITY rises, risk assets typically follow.',
    relationship: 'positive_lead',
    category: 'Liquidity → Equities',
  },
  {
    signal: 'VIXCLS',
    market: 'FOREXCOM:SPXUSD',
    marketName: 'S&P 500',
    narrative: 'VIX spikes precede or coincide with equity selloffs. Sustained elevated VIX indicates persistent stress.',
    relationship: 'inverse',
    category: 'Volatility → Equities',
  },
  {
    signal: 'BAMLH0A0HYM2',
    market: 'FOREXCOM:SPXUSD',
    marketName: 'S&P 500',
    narrative: 'Credit spreads often widen before equity corrections. High yield stress is an early warning for risk assets.',
    relationship: 'inverse_lead',
    category: 'Credit → Equities',
  },
  {
    signal: 'CURVE_2S10S',
    market: 'TVC:US10Y',
    marketName: '10Y Treasury',
    narrative: 'Yield curve inversion (2s10s negative) has preceded every recession since 1970. Watch for steepening after inversion.',
    relationship: 'complex',
    category: 'Rates → Economy',
  },
  {
    signal: 'DTWEXBGS',
    market: 'AMEX:GLD',
    marketName: 'Gold ETF',
    narrative: 'Dollar strength typically pressures gold prices. A weakening dollar often signals gold rallies.',
    relationship: 'inverse',
    category: 'FX → Commodities',
  },
  {
    signal: 'WALCL',
    market: 'NASDAQ:QQQ',
    marketName: 'Tech ETF (QQQ)',
    narrative: 'Fed balance sheet expansion (QE) has historically boosted tech stocks. Watch for QT (shrinking) headwinds.',
    relationship: 'positive',
    category: 'Liquidity → Tech',
  },
];

// ============================================================================
// PAGE STATE
// ============================================================================

let pageState = {
  selectedPair: 0,
  chartMode: 'unknown', // 'advanced' | 'widget' | 'fallback'
  chartInstance: null,
  signalChartInstance: null,
  signalData: null,
  historyData: null,
  loading: true,
  error: null,
};

// ============================================================================
// RENDER FUNCTIONS
// ============================================================================

/**
 * Renders the main page structure
 */
function renderPageStructure() {
  return el('div', { className: 'sia-page' }, [
    // Header
    el('header', { className: 'sia-header' }, [
      el('div', { className: 'sia-header__content' }, [
        el('h1', { className: 'sia-title' }, ['Signals in Action']),
        el('p', { className: 'sia-subtitle' }, [
          'See how CAP macro signals correlate with market movements. ',
          'Select a pair below to visualize the relationship.'
        ]),
      ]),
    ]),
    
    // Main content
    el('main', { className: 'sia-content' }, [
      // Pair selector
      el('nav', { className: 'sia-pairs' }, [
        el('div', { className: 'sia-pairs__label' }, ['Select Correlation:']),
        el('div', { className: 'sia-pairs__list', id: 'pair-selector' }, 
          CORRELATION_PAIRS.map((pair, index) => renderPairButton(pair, index))
        ),
      ]),
      
      // Narrative section
      el('section', { className: 'sia-narrative', id: 'narrative-section' }, [
        renderNarrative(CORRELATION_PAIRS[0]),
      ]),
      
      // Chart container
      el('section', { className: 'sia-chart-section' }, [
        el('div', { className: 'sia-chart-header' }, [
          el('div', { className: 'sia-chart-legend', id: 'chart-legend' }, []),
          el('div', { className: 'sia-chart-mode', id: 'chart-mode-badge' }, []),
        ]),
        el('div', { className: 'sia-chart-container', id: 'chart-container' }, [
          el('div', { className: 'sia-loading', id: 'chart-loading' }, [
            el('span', { className: 'sia-loading__spinner' }, []),
            el('span', {}, ['Initializing charts...']),
          ]),
        ]),
        el('div', { className: 'sia-chart-footer' }, [
          el('p', { className: 'sia-branding-note' }, [
            'Charts powered by TradingView. Signal data from capitulators.io.'
          ]),
        ]),
      ]),
      
      // Signal details card
      el('aside', { className: 'sia-signal-details', id: 'signal-details' }, []),
    ]),
  ]);
}

/**
 * Renders a correlation pair button
 */
function renderPairButton(pair, index) {
  const isActive = index === pageState.selectedPair;
  const signalMeta = SIGNAL_METADATA[pair.signal] || {};
  
  return el('button', {
    className: `sia-pair-btn ${isActive ? 'sia-pair-btn--active' : ''}`,
    'data-index': index,
    onClick: () => handlePairSelect(index),
  }, [
    el('span', { className: 'sia-pair-btn__category' }, [pair.category]),
    el('span', { className: 'sia-pair-btn__signals' }, [
      `${signalMeta.name || pair.signal} → ${pair.marketName}`
    ]),
  ]);
}

/**
 * Renders the narrative section for a pair
 */
function renderNarrative(pair) {
  const signalMeta = SIGNAL_METADATA[pair.signal] || {};
  
  const relationshipBadge = {
    'positive_lead': { text: 'LEADS ↗', class: 'sia-badge--positive' },
    'inverse_lead': { text: 'LEADS ↘', class: 'sia-badge--warning' },
    'positive': { text: 'CORRELATES ↗', class: 'sia-badge--positive' },
    'inverse': { text: 'INVERSE ↘', class: 'sia-badge--danger' },
    'complex': { text: 'COMPLEX ↔', class: 'sia-badge--neutral' },
  }[pair.relationship] || { text: 'RELATED', class: 'sia-badge--neutral' };
  
  return el('div', { className: 'sia-narrative__content' }, [
    el('div', { className: 'sia-narrative__header' }, [
      el('span', { className: `sia-badge ${relationshipBadge.class}` }, [relationshipBadge.text]),
      el('h2', { className: 'sia-narrative__title' }, [pair.category]),
    ]),
    el('p', { className: 'sia-narrative__text' }, [pair.narrative]),
    el('div', { className: 'sia-narrative__meta' }, [
      el('span', { className: 'sia-narrative__signal' }, [
        el('span', { className: 'sia-label' }, ['Signal: ']),
        el('code', {}, [pair.signal]),
        signalMeta.name ? ` (${signalMeta.name})` : '',
      ]),
      el('span', { className: 'sia-narrative__market' }, [
        el('span', { className: 'sia-label' }, ['Market: ']),
        el('code', {}, [pair.marketName]),
      ]),
    ]),
  ]);
}

/**
 * Renders the chart legend
 */
function renderChartLegend(pair) {
  const signalMeta = SIGNAL_METADATA[pair.signal] || {};
  
  return el('div', { className: 'sia-legend' }, [
    el('div', { className: 'sia-legend__item' }, [
      el('span', { className: 'sia-legend__color sia-legend__color--market' }, []),
      el('span', { className: 'sia-legend__label' }, [pair.marketName]),
    ]),
    el('div', { className: 'sia-legend__item' }, [
      el('span', { className: 'sia-legend__color sia-legend__color--signal' }, []),
      el('span', { className: 'sia-legend__label' }, [
        signalMeta.name || pair.signal,
      ]),
    ]),
  ]);
}

/**
 * Renders signal details card
 */
function renderSignalDetails(signalData, pair) {
  if (!signalData) {
    return el('div', { className: 'sia-details-loading' }, ['Loading signal data...']);
  }
  
  const meta = SIGNAL_METADATA[pair.signal] || {};
  const current = signalData.current || {};
  const change = signalData.change || {};
  
  const levelClass = {
    'low': 'sia-level--calm',
    'normal': 'sia-level--normal',
    'elevated': 'sia-level--elevated',
    'high': 'sia-level--stressed',
    'extreme': 'sia-level--extreme',
  }[current.level] || 'sia-level--normal';
  
  return el('div', { className: 'sia-details' }, [
    el('h3', { className: 'sia-details__title' }, [meta.name || pair.signal]),
    el('div', { className: 'sia-details__grid' }, [
      el('div', { className: 'sia-details__item' }, [
        el('span', { className: 'sia-details__label' }, ['Current Value']),
        el('span', { className: 'sia-details__value' }, [
          formatSignalValue(current.value, meta.units)
        ]),
      ]),
      el('div', { className: 'sia-details__item' }, [
        el('span', { className: 'sia-details__label' }, ['Stress Level']),
        el('span', { className: `sia-details__level ${levelClass}` }, [
          (current.level || 'normal').toUpperCase()
        ]),
      ]),
      el('div', { className: 'sia-details__item' }, [
        el('span', { className: 'sia-details__label' }, ['Change']),
        el('span', { 
          className: `sia-details__change ${change.percent >= 0 ? 'sia-up' : 'sia-down'}` 
        }, [
          `${change.percent >= 0 ? '+' : ''}${(change.percent || 0).toFixed(2)}%`
        ]),
      ]),
      el('div', { className: 'sia-details__item' }, [
        el('span', { className: 'sia-details__label' }, ['Last Updated']),
        el('span', { className: 'sia-details__date' }, [
          current.date ? new Date(current.date).toLocaleDateString() : 'N/A'
        ]),
      ]),
    ]),
    meta.description ? el('p', { className: 'sia-details__desc' }, [meta.description]) : null,
  ].filter(Boolean));
}

/**
 * Renders stacked charts (widget mode fallback)
 */
function renderStackedCharts(pair) {
  return el('div', { className: 'sia-stacked-charts' }, [
    // Signal chart (top)
    el('div', { className: 'sia-stacked-signal' }, [
      el('div', { className: 'sia-stacked-label' }, [
        el('span', { className: 'sia-chart-badge sia-chart-badge--cap' }, ['CAP SIGNAL']),
        el('span', {}, [SIGNAL_METADATA[pair.signal]?.name || pair.signal]),
      ]),
      el('div', { 
        className: 'sia-signal-chart', 
        id: 'signal-chart-container',
        style: { height: '200px' }
      }, []),
    ]),
    // Divider
    el('div', { className: 'sia-correlation-divider' }, [
      el('span', { className: 'sia-correlation-arrow' }, ['↕ TIME SYNCED ↕']),
    ]),
    // Market chart (bottom)
    el('div', { className: 'sia-stacked-market' }, [
      el('div', { className: 'sia-stacked-label' }, [
        el('span', { className: 'sia-chart-badge sia-chart-badge--market' }, ['MARKET']),
        el('span', {}, [pair.marketName]),
      ]),
      el('div', { 
        className: 'sia-market-chart', 
        id: 'market-chart-container',
        style: { height: '350px' }
      }, []),
    ]),
  ]);
}

/**
 * Renders setup instructions when library is missing
 */
function renderSetupInstructions() {
  return el('div', { className: 'sia-setup' }, [
    el('div', { className: 'sia-setup__icon' }, ['🔧']),
    el('h3', { className: 'sia-setup__title' }, ['Advanced Charts Setup Required']),
    el('p', { className: 'sia-setup__text' }, [
      'For true signal overlays, install the TradingView Advanced Charts library.'
    ]),
    el('div', { className: 'sia-setup__steps' }, [
      el('ol', {}, [
        el('li', {}, ['Download TradingView Advanced Charts from their GitHub or website']),
        el('li', {}, ['Copy the charting_library folder to public/']),
        el('li', {}, ['Refresh this page']),
      ]),
    ]),
    el('p', { className: 'sia-setup__note' }, [
      'Meanwhile, we\'ll show charts in stacked mode (signal above market).'
    ]),
  ]);
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Formats a signal value with appropriate units
 */
function formatSignalValue(value, units) {
  if (value == null || isNaN(value)) return 'N/A';
  
  switch (units) {
    case '%':
      return `${value.toFixed(2)}%`;
    case 'σ':
      return `${value.toFixed(2)}σ`;
    case 'bps':
      return `${value.toFixed(0)} bps`;
    case 'B':
      return `$${(value / 1000).toFixed(2)}T`;
    case 'M':
      return `$${(value / 1000).toFixed(2)}B`;
    default:
      if (Math.abs(value) >= 1000000000) {
        return `${(value / 1000000000).toFixed(2)}B`;
      } else if (Math.abs(value) >= 1000000) {
        return `${(value / 1000000).toFixed(2)}M`;
      } else if (Math.abs(value) >= 1000) {
        return `${(value / 1000).toFixed(2)}K`;
      }
      return value.toFixed(2);
  }
}

// ============================================================================
// EVENT HANDLERS
// ============================================================================

/**
 * Handles pair selection
 */
async function handlePairSelect(index) {
  if (index === pageState.selectedPair) return;
  
  pageState.selectedPair = index;
  const pair = CORRELATION_PAIRS[index];
  
  // Update pair buttons
  document.querySelectorAll('.sia-pair-btn').forEach((btn, i) => {
    btn.classList.toggle('sia-pair-btn--active', i === index);
  });
  
  // Update narrative
  const narrativeSection = document.getElementById('narrative-section');
  if (narrativeSection) {
    narrativeSection.innerHTML = '';
    narrativeSection.appendChild(renderNarrative(pair));
  }
  
  // Update legend
  const legendContainer = document.getElementById('chart-legend');
  if (legendContainer) {
    legendContainer.innerHTML = '';
    legendContainer.appendChild(renderChartLegend(pair));
  }
  
  // Load signal data
  await loadSignalData(pair.signal);
  
  // Update charts based on mode
  if (pageState.chartMode === 'advanced') {
    await updateAdvancedChart(pair);
  } else {
    await updateStackedCharts(pair);
  }
}

/**
 * Updates Advanced Charts mode
 */
async function updateAdvancedChart(pair) {
  if (!pageState.chartInstance) return;
  
  try {
    // Change main symbol to market
    pageState.chartInstance.setSymbol(pair.market);
    
    // Remove existing overlay if any
    if (pageState.chartInstance.currentOverlay) {
      pageState.chartInstance.removeOverlay(pageState.chartInstance.currentOverlay);
    }
    
    // Add new CAP signal overlay
    const overlay = await pageState.chartInstance.addCAPSignalOverlay(pair.signal);
    pageState.chartInstance.currentOverlay = overlay;
    
    console.log(`[SIA] Advanced chart updated: ${pair.market} + CAP:${pair.signal}`);
  } catch (error) {
    console.error('[SIA] Advanced chart update failed:', error);
  }
}

/**
 * Updates stacked charts (widget mode)
 */
async function updateStackedCharts(pair) {
  // Update signal chart
  const signalContainer = document.getElementById('signal-chart-container');
  if (signalContainer && pageState.historyData) {
    // Destroy old chart
    if (pageState.signalChartInstance) {
      pageState.signalChartInstance.destroy();
    }
    
    // Create new chart
    const chartData = toChartData(pageState.historyData);
    pageState.signalChartInstance = createExpandedChart(signalContainer, chartData, {
      height: 200,
      lineColor: 'var(--tg-liquidity-cyan)',
      areaColor: 'rgba(0, 238, 229, 0.15)',
      showXAxis: true,
      showYAxis: true,
      showGrid: true,
    });
  }
  
  // Update market chart (TradingView widget)
  const marketContainer = document.getElementById('market-chart-container');
  if (marketContainer) {
    // Destroy old widget
    if (pageState.chartInstance) {
      pageState.chartInstance.destroy();
    }
    marketContainer.innerHTML = '';
    
    // Create new widget
    pageState.chartInstance = await createMarketChart(marketContainer, {
      symbol: pair.market,
      interval: 'D',
    });
  }
  
  console.log(`[SIA] Stacked charts updated: ${pair.signal} + ${pair.market}`);
}

/**
 * Loads signal data from API
 */
async function loadSignalData(signalId) {
  const detailsContainer = document.getElementById('signal-details');
  
  try {
    // Fetch current + history
    const [signalResponse, historyResponse] = await Promise.all([
      fetchSignal(signalId),
      fetchSignalHistory(signalId, { days: 365 }),
    ]);
    
    pageState.signalData = signalResponse;
    pageState.historyData = historyResponse.data || [];
    
    // Update details card
    if (detailsContainer) {
      const pair = CORRELATION_PAIRS[pageState.selectedPair];
      detailsContainer.innerHTML = '';
      detailsContainer.appendChild(renderSignalDetails(pageState.signalData, pair));
    }
  } catch (error) {
    console.error('[SIA] Failed to load signal data:', error);
    pageState.signalData = null;
    pageState.historyData = [];
    
    if (detailsContainer) {
      detailsContainer.innerHTML = '';
      detailsContainer.appendChild(
        el('div', { className: 'sia-error' }, [`Failed to load signal: ${error.message}`])
      );
    }
  }
}

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Attempts to initialize Advanced Charts mode
 */
async function tryAdvancedCharts(container, pair) {
  try {
    await loadAdvancedCharts();
    
    if (!isAdvancedChartsLoaded()) {
      return false;
    }
    
    // Create chart container
    const chartDiv = el('div', { 
      id: 'tv-advanced-chart',
      style: { width: '100%', height: '550px' }
    }, []);
    container.appendChild(chartDiv);
    
    // Create chart with market as main symbol
    pageState.chartInstance = await createAdvancedChart(chartDiv, {
      symbol: pair.market,
      interval: '1D',
      height: 550,
      chartType: '2', // Candles for market
    });
    
    // Add CAP signal overlay
    const overlay = await pageState.chartInstance.addCAPSignalOverlay(pair.signal);
    pageState.chartInstance.currentOverlay = overlay;
    
    pageState.chartMode = 'advanced';
    console.log('[SIA] Advanced Charts mode initialized');
    return true;
    
  } catch (error) {
    console.warn('[SIA] Advanced Charts not available:', error.message);
    return false;
  }
}

/**
 * Initializes Widget mode (fallback)
 */
async function initWidgetMode(container, pair) {
  try {
    // Show setup instructions briefly
    const setupNote = renderSetupInstructions();
    container.appendChild(setupNote);
    
    // Add stacked charts container
    const stackedContainer = renderStackedCharts(pair);
    container.appendChild(stackedContainer);
    
    // Load TradingView widget library
    await loadTradingView();
    
    // Create signal chart
    const signalContainer = document.getElementById('signal-chart-container');
    if (signalContainer && pageState.historyData?.length > 0) {
      const chartData = toChartData(pageState.historyData);
      pageState.signalChartInstance = createExpandedChart(signalContainer, chartData, {
        height: 200,
        lineColor: 'var(--tg-liquidity-cyan)',
        areaColor: 'rgba(0, 238, 229, 0.15)',
        showXAxis: true,
        showYAxis: true,
        showGrid: true,
      });
    }
    
    // Create market chart widget
    const marketContainer = document.getElementById('market-chart-container');
    if (marketContainer) {
      pageState.chartInstance = await createMarketChart(marketContainer, {
        symbol: pair.market,
        interval: 'D',
      });
    }
    
    pageState.chartMode = 'widget';
    console.log('[SIA] Widget mode initialized');
    
  } catch (error) {
    console.error('[SIA] Widget mode failed:', error);
    pageState.chartMode = 'fallback';
    container.innerHTML = '';
    container.appendChild(
      el('div', { className: 'sia-error' }, [
        el('h3', {}, ['Charts Unavailable']),
        el('p', {}, ['Unable to load charting library. Please check your connection.']),
      ])
    );
  }
}

/**
 * Initializes the charts
 */
async function initCharts() {
  const container = document.getElementById('chart-container');
  const loadingEl = document.getElementById('chart-loading');
  const modeBadge = document.getElementById('chart-mode-badge');
  
  if (!container) return;
  
  // Check feature flag
  if (!FEATURES.TRADINGVIEW_EMBED?.enabled) {
    if (loadingEl) loadingEl.style.display = 'none';
    container.innerHTML = '';
    container.appendChild(
      el('div', { className: 'sia-error' }, ['TradingView integration is disabled.'])
    );
    return;
  }
  
  const pair = CORRELATION_PAIRS[pageState.selectedPair];
  
  // Load signal data first (needed for stacked mode)
  await loadSignalData(pair.signal);
  
  // Hide loading
  if (loadingEl) loadingEl.style.display = 'none';
  container.innerHTML = '';
  
  // Try Advanced Charts first, fall back to widget mode
  const advancedSuccess = await tryAdvancedCharts(container, pair);
  
  if (!advancedSuccess) {
    await initWidgetMode(container, pair);
  }
  
  // Update legend
  const legendContainer = document.getElementById('chart-legend');
  if (legendContainer) {
    legendContainer.appendChild(renderChartLegend(pair));
  }
  
  // Update mode badge
  if (modeBadge) {
    const modeText = pageState.chartMode === 'advanced' ? 'OVERLAY MODE' : 'STACKED MODE';
    const modeClass = pageState.chartMode === 'advanced' ? 'sia-mode--advanced' : 'sia-mode--stacked';
    modeBadge.appendChild(el('span', { className: `sia-mode-badge ${modeClass}` }, [modeText]));
  }
}

// ============================================================================
// PAGE EXPORTS
// ============================================================================

/**
 * Renders the Signals in Action page
 */
export function renderSignalsInActionPage() {
  // Reset state
  pageState = {
    selectedPair: 0,
    chartMode: 'unknown',
    chartInstance: null,
    signalChartInstance: null,
    signalData: null,
    historyData: null,
    loading: true,
    error: null,
  };
  
  console.log('[SIA] Rendering page');
  return renderPageStructure();
}

/**
 * Initializes page effects (called after DOM mount)
 */
export async function initSignalsInActionEffects() {
  console.log('[SIA] Initializing effects');
  await initCharts();
}

/**
 * Cleans up page resources
 */
export function cleanupSignalsInActionPage() {
  console.log('[SIA] Cleanup');
  
  // Destroy charts
  if (pageState.chartInstance) {
    pageState.chartInstance.destroy?.();
    pageState.chartInstance = null;
  }
  if (pageState.signalChartInstance) {
    pageState.signalChartInstance.destroy?.();
    pageState.signalChartInstance = null;
  }
  
  // Reset state
  pageState = {
    selectedPair: 0,
    chartMode: 'unknown',
    chartInstance: null,
    signalChartInstance: null,
    signalData: null,
    historyData: null,
    loading: true,
    error: null,
  };
}
